<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useWikiPagesStore } from '../stores/wiki_pages'
import type { Citation } from '../stores/threads'

// The "cited pages" cards under a settled assistant answer: one card per
// citation, numbered to match the inline `[n]` chips, showing the page
// path (mono, mint) over its title. Clicking opens the page.
defineProps<{
  citations: readonly Citation[]
}>()

const emit = defineEmits<{
  (e: 'open-citation', path: string): void
}>()

const wikiPages = useWikiPagesStore()
const { pages } = storeToRefs(wikiPages)

// Cards read as the page title rather than the raw markdown path:
// `Anthropic` is what the user knows the page as. Falls back to the path
// when the catalog hasn't loaded or the page isn't (or is no longer) in
// the index, so the card stays useful instead of blanking.
function citationLabel(path: string): string {
  return pages.value.find((p) => p.path === path)?.title || path
}

// The mono line drops the `.md` extension — it's noise next to a title.
function displayPath(path: string): string {
  return path.replace(/\.md$/, '')
}
</script>

<template>
  <ul
    class="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2.5 p-0"
    aria-label="Cited pages"
    data-testid="thread-cited-pages"
  >
    <li v-for="(c, idx) in citations" :key="c.path" class="min-w-0">
      <button
        type="button"
        class="flex min-h-11 w-full cursor-pointer flex-col gap-1 rounded-xl border border-border-subtle bg-surface-bg p-3 text-left text-ink hover:border-border-control hover:bg-surface-hover"
        :title="c.path"
        @click="emit('open-citation', c.path)"
      >
        <span class="w-full truncate font-mono text-xs text-brand-primary"
          >{{ idx + 1 }} · {{ displayPath(c.path) }}</span
        >
        <span class="w-full truncate font-medium">{{ citationLabel(c.path) }}</span>
      </button>
    </li>
  </ul>
</template>
